import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  TrendingUp,
  TrendingDown,
  Users,
  MousePointerClick,
  Eye,
  ArrowUpRight,
  Activity,
  Globe,
  Clock,
  Zap,
  RefreshCw,
  Wifi,
  WifiOff,
  Calendar,
  Download,
  FileText,
  FileSpreadsheet,
  Mail,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  Settings,
  Filter
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Tooltip as MapTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const ANALYTICS_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.staging.integratedtech.ca';

// Build URLSearchParams from a filters object
const buildQueryParams = (filters) => {
  const params = new URLSearchParams();
  params.set('days', filters.days);
  if (filters.country) params.set('country', filters.country);
  if (filters.city)    params.set('city', filters.city);
  if (filters.source)  params.set('source', filters.source);
  if (filters.page)    params.set('page', filters.page);
  return params;
};

const WebAnalytics = ({ fetchWithAuth, BACKEND_URL }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [topPages, setTopPages] = useState(null);
  const [trafficSources, setTrafficSources] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [selectedPage, setSelectedPage] = useState('/pricing');
  const [heatmapDays, setHeatmapDays] = useState(30);
  const [filters, setFilters] = useState({ country: null, city: null, source: null, page: null, days: 7 });
  const [wsConnected, setWsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  // Keep a ref to current filters so polling intervals read the latest value
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  // --- Filter helpers ---
  const applyFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: prev[key] === value ? null : value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(prev => ({ ...prev, country: null, city: null, source: null, page: null }));
  }, []);

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => v && k !== 'days');

  // --- Fetch functions (all accept URLSearchParams) ---
  const fetchOverview = useCallback(async (params) => {
    try {
      const response = await fetchWithAuth(`${ANALYTICS_BACKEND_URL}/api/dashboard/overview?${params}`);
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
        return data;
      } else {
        console.error('Overview endpoint error:', response.status);
        toast.error('Failed to load overview data');
      }
    } catch (error) {
      console.error('Overview fetch error:', error);
      toast.error('Failed to connect to analytics API');
    }
  }, [fetchWithAuth]);

  const fetchRealtime = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${ANALYTICS_BACKEND_URL}/api/dashboard/realtime`);
      if (response.ok) {
        const data = await response.json();
        setRealtime(data);
        return data;
      }
    } catch (error) {
      console.error('Realtime fetch error:', error);
    }
  }, [fetchWithAuth]);

  const fetchHeatmap = useCallback(async (pageUrl, days = heatmapDays) => {
    try {
      const response = await fetchWithAuth(
        `${ANALYTICS_BACKEND_URL}/api/dashboard/heatmap-data?page_url=${encodeURIComponent(pageUrl)}&days=${days}`
      );
      if (response.ok) {
        const data = await response.json();
        setHeatmapData(data);
        return data;
      }
    } catch (error) {
      console.error('Heatmap fetch error:', error);
    }
  }, [fetchWithAuth, heatmapDays]);

  const fetchGeoData = useCallback(async (params) => {
    try {
      const response = await fetchWithAuth(`${ANALYTICS_BACKEND_URL}/api/dashboard/geo-visitors?${params}`);
      if (response.ok) {
        const data = await response.json();
        setGeoData(data);
        return data;
      }
    } catch (error) {
      console.error('Geo data fetch error:', error);
    }
  }, [fetchWithAuth]);

  const fetchTopPages = useCallback(async (params) => {
    try {
      const response = await fetchWithAuth(`${ANALYTICS_BACKEND_URL}/api/dashboard/top-pages?${params}`);
      if (response.ok) {
        const data = await response.json();
        setTopPages(data);
        return data;
      }
    } catch (error) {
      console.error('Top pages fetch error:', error);
    }
  }, [fetchWithAuth]);

  const fetchTrafficSources = useCallback(async (params) => {
    try {
      const response = await fetchWithAuth(`${ANALYTICS_BACKEND_URL}/api/dashboard/traffic-sources?${params}`);
      if (response.ok) {
        const data = await response.json();
        setTrafficSources(data);
        return data;
      }
    } catch (error) {
      console.error('Traffic sources fetch error:', error);
    }
  }, [fetchWithAuth]);

  // --- Primary data effect: re-fetches all filtered endpoints on filter or days change ---
  useEffect(() => {
    let isMounted = true;
    const params = buildQueryParams(filters);

    const loadData = async () => {
      if (!isMounted) return;
      setLoading(true);
      await Promise.all([
        fetchOverview(params),
        fetchRealtime(),
        fetchGeoData(params),
        fetchTopPages(params),
        fetchTrafficSources(params),
      ]);
      if (isMounted) setLoading(false);
    };

    loadData();
    return () => { isMounted = false; };
  }, [filters]);

  // --- Heatmap: only re-fetches when its own controls change ---
  useEffect(() => {
    fetchHeatmap(selectedPage, heatmapDays);
  }, [selectedPage, heatmapDays, fetchHeatmap]);

  // --- WebSocket ---
  useEffect(() => {
    const initWebSocket = () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          console.warn('No auth token for WebSocket connection');
          return;
        }
        const wsProtocol = ANALYTICS_BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
        const wsHost = ANALYTICS_BACKEND_URL.replace(/^https?:\/\//, '');
        const wsUrl = `${wsProtocol}://${wsHost}/api/ws/analytics?token=${token}`;

        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          setWsConnected(true);
          toast.success('Real-time analytics connected');
          pingIntervalRef.current = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send('ping');
            }
          }, 30000);
        };

        wsRef.current.onmessage = (event) => {
          if (event.data === 'pong') return;
          try {
            const { type, payload } = JSON.parse(event.data);
            setLastUpdate(new Date());
            switch (type) {
              case 'realtime_update':
                setRealtime(prev => ({ ...prev, ...payload }));
                break;
              case 'overview_update':
                setOverview(prev => ({ ...prev, ...payload }));
                break;
              case 'new_visitor':
                console.log('New visitor:', payload);
                break;
              case 'new_pageview':
                console.log('New pageview:', payload);
                break;
              case 'new_conversion':
                toast.success(`New conversion: ${payload.event_name}`);
                break;
              default:
                console.log('Unknown WebSocket message type:', type);
            }
          } catch (e) {
            console.error('WebSocket message parse error:', e);
          }
        };

        wsRef.current.onclose = () => {
          setWsConnected(false);
          if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
          reconnectTimeoutRef.current = setTimeout(initWebSocket, 5000);
        };

        wsRef.current.onerror = (error) => {
          console.error('WebSocket error:', error);
          setWsConnected(false);
        };
      } catch (error) {
        console.log('WebSocket connection failed, using polling:', error);
        setWsConnected(false);
      }
    };

    initWebSocket();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    };
  }, []);

  // --- Polling: uses filtersRef so intervals don't need to be recreated on filter change ---
  useEffect(() => {
    const pollInterval = setInterval(() => {
      if (!wsConnected) {
        const params = buildQueryParams(filtersRef.current);
        fetchOverview(params);
        fetchRealtime();
      }
    }, 30000);

    const overviewInterval = setInterval(() => {
      fetchOverview(buildQueryParams(filtersRef.current));
    }, 60000);

    return () => {
      clearInterval(pollInterval);
      clearInterval(overviewInterval);
    };
  }, [fetchRealtime, fetchOverview, wsConnected]);

  // --- Event handlers ---
  const handlePageChange = (page) => {
    setSelectedPage(page);
  };

  const handleDaysRangeChange = (days) => {
    setFilters(prev => ({ ...prev, days: parseInt(days, 10) }));
  };

  const handleHeatmapDaysChange = (days) => {
    setHeatmapDays(parseInt(days, 10));
  };

  const handleRefresh = async () => {
    setLoading(true);
    const params = buildQueryParams(filters);
    await Promise.all([
      fetchOverview(params),
      fetchRealtime(),
      fetchHeatmap(selectedPage, heatmapDays),
      fetchGeoData(params),
      fetchTopPages(params),
      fetchTrafficSources(params),
    ]);
    setLoading(false);
    setLastUpdate(new Date());
    toast.success('Data refreshed');
  };

  const handleExportCSV = async (dataType = 'overview') => {
    try {
      const token = localStorage.getItem('auth_token');
      const params = buildQueryParams(filters);
      params.set('data_type', dataType);
      const response = await fetch(
        `${ANALYTICS_BACKEND_URL}/api/dashboard/export/csv?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics_${dataType}_${filters.days}days.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`${dataType} data exported to CSV`);
      } else {
        toast.error('Failed to export CSV');
      }
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Export failed');
    }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${ANALYTICS_BACKEND_URL}/api/dashboard/export/pdf?days=${filters.days}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics_report_${filters.days}days.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Report exported to PDF');
      } else {
        toast.error('Failed to export PDF');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Export failed');
    }
  };

  const fetchSchedules = useCallback(async () => {
    setSchedulesLoading(true);
    try {
      const response = await fetchWithAuth(`${ANALYTICS_BACKEND_URL}/api/dashboard/reports/schedules`);
      if (response.ok) {
        const data = await response.json();
        setSchedules(data.schedules || []);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    } finally {
      setSchedulesLoading(false);
    }
  }, [fetchWithAuth]);

  const createSchedule = async (scheduleData) => {
    try {
      const response = await fetchWithAuth(`${ANALYTICS_BACKEND_URL}/api/dashboard/reports/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleData)
      });
      if (response.ok) {
        toast.success('Report schedule created');
        fetchSchedules();
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create schedule');
        return false;
      }
    } catch (error) {
      console.error('Create schedule error:', error);
      toast.error('Failed to create schedule');
      return false;
    }
  };

  const toggleSchedule = async (scheduleId) => {
    try {
      const response = await fetchWithAuth(
        `${ANALYTICS_BACKEND_URL}/api/dashboard/reports/schedule/${scheduleId}/toggle`,
        { method: 'POST' }
      );
      if (response.ok) {
        toast.success('Schedule updated');
        fetchSchedules();
      } else {
        toast.error('Failed to update schedule');
      }
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const deleteSchedule = async (scheduleId) => {
    try {
      const response = await fetchWithAuth(
        `${ANALYTICS_BACKEND_URL}/api/dashboard/reports/schedule/${scheduleId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        toast.success('Schedule deleted');
        fetchSchedules();
      } else {
        toast.error('Failed to delete schedule');
      }
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  useEffect(() => {
    if (showScheduleModal) fetchSchedules();
  }, [showScheduleModal, fetchSchedules]);

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="web-analytics-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading web analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="web-analytics-dashboard">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Web Analytics</h2>
          <p className="text-muted-foreground mt-2">
            Real-time visitor insights, conversions, and engagement metrics
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={String(filters.days)} onValueChange={handleDaysRangeChange}>
            <SelectTrigger className="w-[140px]" data-testid="days-range-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>

          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
            wsConnected
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
          }`}>
            {wsConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            {wsConnected ? 'Live' : 'Polling'}
          </div>

          {lastUpdate && (
            <span className="text-sm text-muted-foreground">
              Updated {lastUpdate.toLocaleTimeString()}
            </span>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          <div className="flex items-center gap-1 border-l border-border pl-4">
            <Button variant="outline" size="sm" onClick={() => handleExportCSV('overview')} data-testid="export-csv-btn" title="Export to CSV">
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn" title="Export to PDF">
              <FileText className="w-4 h-4 mr-1" />
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowScheduleModal(true)} data-testid="schedule-reports-btn" title="Schedule Email Reports">
              <Mail className="w-4 h-4 mr-1" />
              Schedule
            </Button>
          </div>
        </div>
      </div>

      {/* Active Filter Badges */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Filter className="w-4 h-4" />
            Filters:
          </span>
          {Object.entries(filters)
            .filter(([k, v]) => v && k !== 'days')
            .map(([key, val]) => (
              <span
                key={key}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
              >
                {key}: {val}
                <button onClick={() => applyFilter(key, null)} className="ml-0.5 hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs h-7">
            Clear all
          </Button>
        </div>
      )}

      {/* Schedule Reports Modal */}
      {showScheduleModal && (
        <ScheduleReportsModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          schedules={schedules}
          loading={schedulesLoading}
          onCreate={createSchedule}
          onToggle={toggleSchedule}
          onDelete={deleteSchedule}
        />
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard title="Total Visitors" value={overview?.total_visitors || 0} change={overview?.visitors_change || 0} icon={Users} color="blue" testId="kpi-total-visitors" />
        <KPICard title="Conversions" value={overview?.conversions || 0} change={overview?.conversions_change || 0} icon={MousePointerClick} color="green" format="number" testId="kpi-conversions" />
        <KPICard title="Bounce Rate" value={overview?.bounce_rate || 0} change={overview?.bounce_rate_change || 0} icon={TrendingDown} color="orange" format="percent" invertChange testId="kpi-bounce-rate" />
        <KPICard title="Avg Session Duration" value={overview?.avg_session_duration || 0} change={overview?.session_duration_change || 0} icon={Clock} color="purple" format="duration" testId="kpi-session-duration" />
      </div>

      {/* Real-time Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="realtime-visitors-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-500" />
                Real-time Visitors
              </CardTitle>
              <div className="flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <span className="text-2xl font-bold text-foreground">{realtime?.active_visitors || 0}</span>
                <span className="text-muted-foreground">active now</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={realtime?.visitors_timeline || []}>
                <defs>
                  <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="time" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                <Area type="monotone" dataKey="visitors" stroke="#10b981" fill="url(#colorVisitors)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="active-sessions-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Active Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="text-5xl font-bold text-foreground">{realtime?.active_sessions || 0}</div>
                <p className="text-muted-foreground mt-1">Total Sessions</p>
              </div>
              <div className="space-y-3">
                {realtime?.sessions_by_source?.map((source, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getSourceColor(source.name)}`}></div>
                      <span className="text-sm">{source.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{source.count}</span>
                      <span className="text-xs text-muted-foreground">
                        ({((source.count / (realtime?.active_sessions || 1)) * 100).toFixed(0)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-medium mb-2">Top Pages Now</h4>
                {realtime?.top_pages?.slice(0, 5).map((page, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1">
                    <span className="text-sm text-muted-foreground truncate max-w-[150px]">{page.path}</span>
                    <span className="text-sm font-medium">{page.visitors}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Traffic Trends */}
      <Card data-testid="traffic-trends-card">
        <CardHeader>
          <CardTitle>
            Traffic Trends ({formatDaysLabel(filters.days)}{hasActiveFilters ? ' — filtered' : ''})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={overview?.daily_traffic || []}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="date" className="text-xs" />
              <YAxis className="text-xs" />
              <Tooltip contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', borderRadius: '8px' }} />
              <Legend />
              <Line type="monotone" dataKey="visitors" stroke="#3b82f6" name="Visitors" strokeWidth={2} />
              <Line type="monotone" dataKey="pageviews" stroke="#10b981" name="Page Views" strokeWidth={2} />
              <Line type="monotone" dataKey="conversions" stroke="#f59e0b" name="Conversions" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top Pages + Traffic Sources side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Top Pages */}
        <Card data-testid="top-pages-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-500" />
                Top Pages
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                Click a row to filter
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {!topPages?.pages?.length ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                No page data available
              </div>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-12 text-xs font-medium text-muted-foreground px-3 pb-2 border-b border-border">
                  <span className="col-span-6">Page</span>
                  <span className="col-span-3 text-right">Views</span>
                  <span className="col-span-3 text-right">Unique</span>
                </div>
                {topPages.pages.map((page, idx) => (
                  <button
                    key={idx}
                    onClick={() => applyFilter('page', page.path)}
                    className={`w-full grid grid-cols-12 items-center px-3 py-2.5 rounded-lg text-left transition-colors hover:bg-muted/70 ${
                      filters.page === page.path ? 'bg-primary/10 ring-1 ring-primary/20' : ''
                    }`}
                  >
                    <span className="col-span-6 text-sm font-medium truncate">{page.path}</span>
                    <span className="col-span-3 text-sm text-right">{page.views.toLocaleString()}</span>
                    <span className="col-span-3 text-sm text-muted-foreground text-right">{page.unique_visitors.toLocaleString()}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Traffic Sources */}
        <Card data-testid="traffic-sources-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5 text-green-500" />
                Traffic Sources
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                {trafficSources?.total_sessions
                  ? `${trafficSources.total_sessions.toLocaleString()} sessions total`
                  : 'Click a row to filter'}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {!trafficSources?.sources?.length ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                No source data available
              </div>
            ) : (
              <div className="space-y-2">
                {trafficSources.sources.map((source, idx) => (
                  <button
                    key={idx}
                    onClick={() => applyFilter('source', source.name)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-muted/70 text-left ${
                      filters.source === source.name ? 'bg-primary/10 ring-1 ring-primary/20' : ''
                    }`}
                  >
                    <div className={`w-3 h-3 rounded-full shrink-0 ${getSourceColor(source.name)}`} />
                    <span className="text-sm font-medium flex-1">{source.name}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${getSourceColor(source.name)}`}
                          style={{ width: `${source.percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-10 text-right">{source.percentage}%</span>
                      <span className="text-sm font-semibold w-8 text-right">{source.sessions}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Visitor Locations Map */}
      <Card data-testid="visitor-locations-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              Visitor Locations
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              {filters.country ? `Showing: ${filters.country}` : `${formatDaysLabel(filters.days)} — click a marker to filter`}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <VisitorLocationMap
            data={geoData}
            activeCountry={filters.country}
            onLocationClick={(country) => applyFilter('country', country)}
          />
        </CardContent>
      </Card>

      {/* Heatmap */}
      <Card data-testid="heatmap-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-500" />
              Click Heatmap
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={selectedPage} onValueChange={handlePageChange}>
                <SelectTrigger className="w-[160px]" data-testid="heatmap-page-select">
                  <SelectValue placeholder="Select page" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="/pricing">Pricing Page</SelectItem>
                  <SelectItem value="/features">Features Page</SelectItem>
                  <SelectItem value="/home">Home Page</SelectItem>
                  <SelectItem value="/contact">Contact Page</SelectItem>
                  <SelectItem value="/signup">Signup Page</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(heatmapDays)} onValueChange={handleHeatmapDaysChange}>
                <SelectTrigger className="w-[120px]" data-testid="heatmap-days-select">
                  <SelectValue placeholder="Days" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="relative bg-muted rounded-lg p-4 min-h-[400px]">
              <div className="absolute inset-0 p-4">
                <HeatmapVisualization data={heatmapData} page={selectedPage} />
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">Click Distribution</h4>
              <div className="space-y-3">
                {heatmapData?.click_zones?.map((zone, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{zone.element}</span>
                      <span className="font-medium">{zone.clicks} clicks</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${getHeatColor(zone.intensity)}`} style={{ width: `${zone.percentage}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-border">
                <h4 className="font-semibold text-foreground mb-3">Page Metrics</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-sm text-muted-foreground">Total Clicks</p>
                    <p className="text-2xl font-bold">{heatmapData?.total_clicks || 0}</p>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-sm text-muted-foreground">Click-through Rate</p>
                    <p className="text-2xl font-bold">{heatmapData?.ctr || 0}%</p>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-sm text-muted-foreground">Avg Scroll Depth</p>
                    <p className="text-2xl font-bold">{heatmapData?.avg_scroll_depth || 0}%</p>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-sm text-muted-foreground">Engagement Score</p>
                    <p className="text-2xl font-bold">{heatmapData?.engagement_score || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

// --- Sub-components ---

const KPICard = ({ title, value, change, icon: Icon, color, format = 'number', invertChange = false, testId }) => {
  const formatValue = (val) => {
    switch (format) {
      case 'percent':   return `${val.toFixed(1)}%`;
      case 'duration': {
        const mins = Math.floor(val / 60);
        const secs = val % 60;
        return `${mins}m ${secs}s`;
      }
      default: return val.toLocaleString();
    }
  };

  const isPositive = invertChange ? change < 0 : change > 0;
  const colorClasses = {
    blue:   'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    green:  'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
  };

  return (
    <Card data-testid={testId}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold text-foreground">{formatValue(value)}</p>
            <div className={`flex items-center gap-1 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span>{Math.abs(change).toFixed(1)}%</span>
              <span className="text-muted-foreground">vs last period</span>
            </div>
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const HeatmapVisualization = ({ data, page }) => {
  if (!data?.click_points) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No heatmap data available
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 rounded-lg overflow-hidden">
      <div className="absolute inset-4 bg-white dark:bg-slate-950 rounded shadow-lg">
        <div className="h-12 bg-slate-100 dark:bg-slate-800 border-b flex items-center px-4 gap-2">
          <div className="w-20 h-4 bg-slate-300 dark:bg-slate-600 rounded"></div>
          <div className="flex-1"></div>
          <div className="w-16 h-4 bg-slate-300 dark:bg-slate-600 rounded"></div>
          <div className="w-16 h-4 bg-slate-300 dark:bg-slate-600 rounded"></div>
        </div>
        <div className="p-4 space-y-4">
          <div className="w-3/4 h-8 bg-slate-200 dark:bg-slate-700 rounded"></div>
          <div className="w-1/2 h-4 bg-slate-100 dark:bg-slate-800 rounded"></div>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <div className="h-20 bg-slate-100 dark:bg-slate-800 rounded"></div>
            <div className="h-20 bg-slate-100 dark:bg-slate-800 rounded"></div>
            <div className="h-20 bg-slate-100 dark:bg-slate-800 rounded"></div>
          </div>
          <div className="w-32 h-10 bg-blue-500 rounded mx-auto mt-4"></div>
        </div>
        {data.click_points.map((point, idx) => (
          <div
            key={idx}
            className="absolute rounded-full animate-pulse"
            style={{
              left: `${point.x}%`,
              top: `${point.y}%`,
              width: `${Math.max(20, point.intensity * 2)}px`,
              height: `${Math.max(20, point.intensity * 2)}px`,
              background: `radial-gradient(circle, ${getHeatColorRGB(point.intensity)} 0%, transparent 70%)`,
              transform: 'translate(-50%, -50%)',
              opacity: Math.min(0.8, point.intensity / 100)
            }}
          />
        ))}
      </div>
    </div>
  );
};

const VisitorLocationMap = ({ data, activeCountry, onLocationClick }) => {
  if (!data?.locations?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground gap-3">
        <Globe className="w-12 h-12 opacity-30" />
        <p className="font-medium">No geographic data available</p>
        <p className="text-xs text-center max-w-sm">
          Ensure your analytics backend is collecting visitor IP geolocation data via the{' '}
          <code className="bg-muted px-1 rounded text-xs">/api/dashboard/geo-visitors</code> endpoint.
        </p>
      </div>
    );
  }

  const maxVisitors = Math.max(...data.locations.map(l => l.visitors));

  return (
    <div className="space-y-4">
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: '400px', width: '100%', borderRadius: '8px' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {data.locations.map((loc, idx) => {
          const isActive = activeCountry === loc.country;
          return (
            <CircleMarker
              key={idx}
              center={[loc.lat, loc.lng]}
              radius={Math.max(6, Math.min(30, (loc.visitors / maxVisitors) * 30))}
              fillColor={isActive ? '#f59e0b' : '#3b82f6'}
              fillOpacity={isActive ? 0.75 : 0.55}
              color={isActive ? '#d97706' : '#2563eb'}
              weight={isActive ? 2 : 1}
              eventHandlers={{ click: () => onLocationClick && onLocationClick(loc.country) }}
            >
              <MapTooltip>
                <div className="text-sm">
                  <strong>{loc.city ? `${loc.city}, ${loc.country}` : loc.country}</strong>
                  <br />
                  {loc.visitors.toLocaleString()} visitors &mdash; {loc.percentage}%
                  {onLocationClick && <div className="text-xs text-muted-foreground mt-0.5">Click to filter</div>}
                </div>
              </MapTooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>

      <div>
        <h4 className="font-semibold text-foreground mb-3">Top Locations</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {data.locations.slice(0, 10).map((loc, idx) => (
            <button
              key={idx}
              onClick={() => onLocationClick && onLocationClick(loc.country)}
              className={`flex items-center justify-between rounded-lg px-3 py-2 transition-colors hover:bg-muted/70 ${
                activeCountry === loc.country ? 'bg-primary/10 ring-1 ring-primary/20' : 'bg-muted'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-muted-foreground w-5 text-right">{idx + 1}</span>
                <span className="text-sm font-medium truncate max-w-[140px]">
                  {loc.city ? `${loc.city}, ${loc.country}` : loc.country}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-16 h-1.5 bg-background rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${loc.percentage}%` }} />
                </div>
                <span className="text-xs text-muted-foreground w-8 text-right">{loc.percentage}%</span>
                <span className="text-sm font-semibold w-16 text-right">{loc.visitors.toLocaleString()}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- Helper functions ---
const formatDaysLabel = (days) => days === 1 ? 'Last 24 hours' : `Last ${days} days`;

const getSourceColor = (source) => {
  const colors = {
    'Direct':  'bg-blue-500',
    'Organic': 'bg-green-500',
    'Referral':'bg-purple-500',
    'Social':  'bg-pink-500',
    'Paid':    'bg-orange-500',
    'Email':   'bg-cyan-500'
  };
  return colors[source] || 'bg-gray-500';
};

const getHeatColor = (intensity) => {
  if (intensity >= 80) return 'bg-red-500';
  if (intensity >= 60) return 'bg-orange-500';
  if (intensity >= 40) return 'bg-yellow-500';
  if (intensity >= 20) return 'bg-green-500';
  return 'bg-blue-500';
};

const getHeatColorRGB = (intensity) => {
  if (intensity >= 80) return 'rgba(239, 68, 68, 0.8)';
  if (intensity >= 60) return 'rgba(249, 115, 22, 0.7)';
  if (intensity >= 40) return 'rgba(234, 179, 8, 0.6)';
  if (intensity >= 20) return 'rgba(34, 197, 94, 0.5)';
  return 'rgba(59, 130, 246, 0.4)';
};

// --- Schedule Reports Modal ---
const ScheduleReportsModal = ({ isOpen, onClose, schedules, loading, onCreate, onToggle, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    frequency: 'weekly',
    email: '',
    report_type: 'full',
    day_of_week: 1,
    time: '09:00',
    include_csv: true,
    report_days: 7
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) { toast.error('Email is required'); return; }
    const success = await onCreate(formData);
    if (success) {
      setShowForm(false);
      setFormData({ frequency: 'weekly', email: '', report_type: 'full', day_of_week: 1, time: '09:00', include_csv: true, report_days: 7 });
    }
  };

  const frequencyLabels = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
  const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="schedule-modal">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Scheduled Email Reports</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="w-full mb-4" data-testid="add-schedule-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add New Schedule
            </Button>
          )}

          {showForm && (
            <Card className="mb-4" data-testid="schedule-form">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">New Report Schedule</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-sm font-medium">Email Address *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="admin@company.com"
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background"
                        required
                        data-testid="schedule-email-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Frequency</label>
                      <select
                        value={formData.frequency}
                        onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background"
                        data-testid="schedule-frequency-select"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    {formData.frequency === 'weekly' && (
                      <div>
                        <label className="text-sm font-medium">Day</label>
                        <select
                          value={formData.day_of_week}
                          onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                          className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background"
                        >
                          {dayLabels.map((day, idx) => <option key={idx} value={idx}>{day}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium">Time</label>
                      <input
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Report Type</label>
                      <select
                        value={formData.report_type}
                        onChange={(e) => setFormData({ ...formData, report_type: e.target.value })}
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background"
                      >
                        <option value="full">Full Report</option>
                        <option value="summary">Summary</option>
                        <option value="kpi_only">KPIs Only</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Data Range</label>
                      <select
                        value={formData.report_days}
                        onChange={(e) => setFormData({ ...formData, report_days: parseInt(e.target.value) })}
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background"
                      >
                        <option value={7}>Last 7 days</option>
                        <option value={14}>Last 14 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                      </select>
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="include_csv"
                        checked={formData.include_csv}
                        onChange={(e) => setFormData({ ...formData, include_csv: e.target.checked })}
                        className="rounded"
                      />
                      <label htmlFor="include_csv" className="text-sm">Attach CSV data files</label>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button type="submit" data-testid="save-schedule-btn">Create Schedule</Button>
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">
              {schedules.length > 0 ? 'Active Schedules' : 'No scheduled reports'}
            </h4>
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : (
              schedules.map((schedule) => (
                <div key={schedule.id} className="flex items-center justify-between p-3 bg-muted rounded-lg" data-testid={`schedule-item-${schedule.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{schedule.email}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        schedule.is_active
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {schedule.is_active ? 'Active' : 'Paused'}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {frequencyLabels[schedule.frequency]} at {schedule.time}
                      {schedule.frequency === 'weekly' && ` on ${dayLabels[schedule.day_of_week]}`}
                      {' • '}{schedule.report_type} report • {schedule.report_days} days
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => onToggle(schedule.id)} title={schedule.is_active ? 'Pause' : 'Activate'}>
                      {schedule.is_active
                        ? <ToggleRight className="w-5 h-5 text-green-500" />
                        : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onDelete(schedule.id)} className="text-red-500 hover:text-red-600" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="p-4 border-t border-border bg-muted/30">
          <p className="text-xs text-muted-foreground">
            Reports are sent via email at the scheduled time. Ensure SMTP is configured on the server.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WebAnalytics;
